/**
 * Preview Info Component
 *
 * Displays file metadata, download options, and editable title/description.
 * Used in both desktop side panel and mobile bottom sheet.
 */

import {
  Component,
  DestroyRef,
  input,
  output,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDownload, faBullhorn } from '@fortawesome/pro-duotone-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { TabStripEllipsisDirective } from '../../../../directives/tab-strip-ellipsis.directive';

import { Item } from '../../../../models/item';
import { can } from '../../../../models/access.model';
import { LocalizedValue } from '../../../localized-input/localized-input.component';
import { LabelFieldComponent } from '../../../label-field/label-field.component';
import { LocaleService } from '../../../../services/locale.service';
import { SystemAccessService } from '../../../../services/system-access.service';
import { MembershipService } from '../../../../services/membership.service';
import { shouldShowUnpublishedIndicator } from '../../../../utils/publish-filter.utils';
import { ItemService } from '../../../../services/item.service';
import { FilePreviewService } from '../../../../services/file-preview.service';
import { TemporalContextService } from '../../../../services/temporal-context.service';
import { GraphQLClient } from '../../../../graphql/client';
import { MutationError, resolveErrorMessage } from '@branduals/graphql-errors';
import { CAMPAIGNS_USING_ASSET, CampaignsUsingAssetResponse, CampaignUsingAsset } from '../../../../graphql/campaign-operations';
import { isFile, isComplianceLocked } from '../../../../models/item';
import { keepSelectionOffered } from '../../../../utils/keep-selection-offered';
import { commentLandingFor } from '../../../topbar/topbar-routing';
import { FormatSizePipe } from '../../../../pipes/format-size.pipe';
import { CommentThreadComponent } from '../../../comments/comment-thread/comment-thread.component';
import { MediaMasonryComponent } from '../../../media-masonry/media-masonry.component';
import { TagsDisplayComponent } from '../../../tags-display/tags-display.component';
import { GovernanceSectionComponent } from '../governance-section/governance-section.component';
import { ItemHistorySectionComponent } from '../../../item-history/item-history-section.component';
import { FileVersionsSectionComponent } from '../../../item-history/file-versions-section.component';
import { ItemHistoryService } from '../../../../services/item-history.service';
import { DisplaySearchResult } from '../../../../services/search.service';
import type { FilePreviewInfoOptions, PreviewSectionName, SidebarTabSpec } from '../../../../services/file-preview-context';
import { commentsArmed, threadParentFor } from '../../../../services/file-preview-context';
import type { Comment, CommentAnchor } from '../../../../models/comment.models';

import type { DownloadOption } from '../../../../utils/file-details.utils';
import { raiseTranslationSavedToast } from '../../../../utils/translation-saved-toast.utils';
import { raiseDraftRestoreToast } from '../../../../utils/draft-restore-toast.utils';
import { ActionToastService } from '../../../action-toast/action-toast.service';
import type { LabelSubmission } from '../../../label-field/label-field.component';
import { DisplayDatePipe } from '../../../../pipes/display-date.pipe';

/** The per-field failure sentence a refused translate-door write speaks (the inline handlers' own keys). */
const TRANSLATE_SAVE_ERROR_KEYS = {
  title: 'filePreview.titleSaveError',
  caption: 'filePreview.captionSaveError',
  description: 'filePreview.descriptionSaveError',
} as const;

@Component({
  selector: 'app-preview-info',
  standalone: true,
  imports: [
    DisplayDatePipe,
    TabStripEllipsisDirective,
    CommonModule,
    NgComponentOutlet,
    FontAwesomeModule,
    TranslateModule,
    LabelFieldComponent,
    FormatSizePipe,
    CommentThreadComponent,
    MediaMasonryComponent,
    TagsDisplayComponent,
    GovernanceSectionComponent,
    ItemHistorySectionComponent,
    FileVersionsSectionComponent,
    ProgressSpinnerModule,
    Tabs, TabList, Tab, TabPanels, TabPanel,
  ],
  templateUrl: './preview-info.component.html',
  styleUrls: ['./preview-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreviewInfoComponent {
  private readonly localeService = inject(LocaleService);
  private readonly access = inject(SystemAccessService);
  private readonly itemService = inject(ItemService);
  private readonly messageService = inject(MessageService);
  private readonly actionToastService = inject(ActionToastService);
  private readonly translateService = inject(TranslateService);
  private readonly previewService = inject(FilePreviewService);
  private readonly graphql = inject(GraphQLClient);
  private readonly temporal = inject(TemporalContextService);
  private readonly membership = inject(MembershipService);
  /** A public visitor reads none of the member-only NOW surfaces: comments, history, versions, the similar strip and the Rights & compliance zone hide. */
  private readonly publicMode = this.membership.publicMode;
  private readonly itemHistory = inject(ItemHistoryService);
  private readonly destroyRef = inject(DestroyRef);
  /**
   * Set on destroy. Only the ROLLBACK EMIT of an in-flight commit stands down after teardown: an
   * output cannot deliver once its component is destroyed (Angular drops the emit), and the host
   * that would have received the rollback is gone. The failure TOAST never stands down — it rides
   * the app-level toast mount and shows wherever the member is now (ruled 2026-09-01).
   */
  private destroyed = false;

  // ============================================================================
  // Campaign back-references — "Used in N campaigns". Lazily loaded once per file
  // item (the deck's "every asset pays back forever" surface). Gated to file types.
  // Planned: the chip that renders this list — today NOTHING reads `campaignsUsing`
  // in a template, so the read is a round trip per file preview with no surface; the
  // silence-class census rosters it BACKGROUND with that flag, and the failure
  // grammar binds the read the day the chip is built.
  // ============================================================================
  readonly campaignsUsing = signal<CampaignUsingAsset[]>([]);
  private campaignsLoadedFor: string | null = null;

  /** Sidebar tab identifier — 'info' | 'comments' | 'extra:{label}' for context-provided tabs. */
  readonly activeTab = signal<string>('info');

  /** Context-provided extra tabs (beyond Info + Comments). */
  readonly extraTabs = computed<readonly SidebarTabSpec[]>(() =>
    this.previewService.context()?.extraSidebarTabs ?? []
  );

  // ============================================================================
  // Section visibility (one computed per section — methods called from templates
  // would re-execute every change-detection cycle in this OnPush + signals tree).
  // ============================================================================

  private sectionVisible(name: PreviewSectionName, fallback: boolean): boolean {
    const override = this.previewService.context()?.infoOptions?.sections?.[name];
    return override ?? fallback;
  }

  readonly showTitle       = computed<boolean>(() => this.sectionVisible('title',       true));
  readonly showCaption     = computed<boolean>(() => this.sectionVisible('caption',     false));
  readonly showDescription = computed<boolean>(() => this.sectionVisible('description', true));
  readonly showDetails     = computed<boolean>(() => this.sectionVisible('details',     true));
  // The download options follow the item's OWN role — a View-only reader (a public visitor of a brand
  // that grants View alone) sees none, whatever the context offers.
  readonly showDownloads   = computed<boolean>(() => this.sectionVisible('downloads',   true) && can.download(this.item().effectiveRole));
  // Comments are a NOW-clock surface — never shown beside asOf content (the rule is
  // one clock per screen; hides tab AND composer together via the section mechanism) —
  // and a member's surface: a public visitor reads no conversation.
  readonly showComments    = computed<boolean>(() => this.sectionVisible('comments',    true) && !this.temporal.active() && !this.publicMode());
  // Similar is a NOW-clock surface (index-backed, no asOf) — comments-gate twin; the server refuses
  // the similar-items read to a public visitor, so the tab never offers a door it cannot open.
  readonly showSimilar     = computed<boolean>(() => this.sectionVisible('similar',     true) && !this.temporal.active() && !this.publicMode());
  readonly showLicense     = computed<boolean>(() => this.sectionVisible('license',     true));
  readonly showProvenance  = computed<boolean>(() => this.sectionVisible('provenance',  true));
  // The Rights & compliance zone (license · provenance · lock) is a members' surface: the projection fences the three
  // fields to a public visitor, and a fenced null must never render as "None" / "Unlocked". File items alone carry it.
  readonly showGovernance  = computed<boolean>(() => this.isFileItem() && !this.publicMode());
  // History stays visible IN Time-travel mode: clock-free temporal data, bounded server-side
  // to the viewed instant via occurredBefore. A member's surface — hidden from a public visitor.
  readonly showHistory     = computed<boolean>(() => this.sectionVisible('history',     true) && !this.publicMode());
  // Versions tab: tab chrome renders independently of panel content — the
  // HOST owns version-existence so a once-uploaded file never shows a dead tab (L-021).
  // Consumes the SAME memoized fetch the section renders from (one wire call, two consumers).
  // NOW-only surface (comments-gate twin), a member's surface (hidden from a public visitor).
  private readonly hasPriorVersions = signal(false);
  readonly showVersions    = computed<boolean>(() =>
    this.sectionVisible('versions', true) && this.isFileItem() && !this.temporal.active() && !this.publicMode() && this.hasPriorVersions());

  /** License applies to file assets only — hide the section on non-file previews. */
  readonly isFileItem = computed<boolean>(() => isFile(this.item()));

  /** i18n key for the Comments tab — a context may relabel it (Reviews → "Feedback"). */
  readonly commentsLabel = computed<string>(() =>
    this.previewService.context()?.infoOptions?.commentsLabel ?? 'filePreview.sidebar.comments');

  /** Context opt-in: Versions tab visible but promote hidden (see FilePreviewInfoOptions). */
  readonly versionsReadOnly = computed<boolean>(() =>
    this.previewService.context()?.infoOptions?.versionsReadOnly === true);

  /** Whether a sidebar tab is on the strip right now — the gates above, plus the context's extra tabs. */
  private isTabOffered(tab: string): boolean {
    switch (tab) {
      case 'info': return true;
      case 'similar': return this.showSimilar();
      case 'comments': return this.showComments();
      case 'history': return this.showHistory();
      case 'versions': return this.showVersions();
      default: return this.extraTabs().some(t => `extra:${t.label}` === tab);
    }
  }

  /** The ONE guard every conditionally-offered tab strip shares: a member on Versions who arrows the filmstrip
   *  to a single-version file (or whose versions re-read fails after a promote) lands on Info the moment the
   *  tab stops being offered — never a selection on a tab that vanished, with an empty panel and a stranded
   *  underline; a tab offered again later is theirs to choose. */
  private readonly tabGuard = keepSelectionOffered(this.activeTab, tab => this.isTabOffered(tab), 'info');

  // Comment anchor — a context may override per-item (Reviews threads on the current ENTRY);
  // otherwise it defaults to the previewed item itself, as placed.
  private readonly resolvedCommentParent = computed<{ parentId: string; contextKey: string }>(() =>
    threadParentFor(this.previewService.context(), this.item()));
  readonly commentParentId   = computed<string>(() => this.resolvedCommentParent().parentId);
  readonly commentContextKey = computed<string>(() => this.resolvedCommentParent().contextKey);

  // ── Annotation handoff: the viewer's annotate gesture parks an anchor on the preview
  // service; the thread's composer carries it into the next post. ──
  readonly pendingAnchor = computed<CommentAnchor | null>(() => this.previewService.pendingAnchor());

  /** The one-shot exact-comment focus a bell or copied comment link parked on the
   *  service (via the container's preview URL bridge) — handed to the thread; its
   *  handled-ack clears it so a later manual tab open never re-flashes. */
  readonly pendingCommentFocus = computed<string | null>(() => this.previewService.requestedCommentFocus());

  /** Per-comment copy-link builder — only where the address is EXACT: the comment's
   *  parent is the previewed FILE itself (the DAM doors), whose deep link redirects to
   *  the container with `?item=` and the `?comment=` focus riding beside it.
   *  Pillar-context previews (entry/asset threads) mint no comment address yet —
   *  no builder, no affordance (a copied link must never under-deliver). */
  readonly commentLinkBuilder = computed<((c: Comment) => string) | null>(() => {
    const item = this.item();
    if (!isFile(item) || this.resolvedCommentParent().parentId !== item.id) return null;
    const lens = this.localeService.slugLocale();
    return (c: Comment) => window.location.origin + commentLandingFor(item, lens, c.urlId).url;
  });

  onCommentFocusHandled(): void {
    this.previewService.requestedCommentFocus.set(null);
  }

  /** Context hook: per-comment revision-round tag for the thread (Reviews only). */
  readonly commentRoundTagFor = computed<((c: Comment) => number | null) | null>(() =>
    this.previewService.context()?.commentRoundTagFor ?? null);

  /** A comment posted through the thread: the consumed anchor clears and the viewer's
   *  overlay re-fetches its pins. */
  onCommentPosted(): void {
    this.previewService.pendingAnchor.set(null);
    this.previewService.annotationsVersion.update(n => n + 1);
  }

  /** An in-thread mutation (delete / resolve / reopen) changed the comment set — the
   *  viewer's overlay re-fetches its pins, so a deleted pin leaves the media at once. */
  onCommentMutated(): void {
    this.previewService.annotationsVersion.update(n => n + 1);
  }

  /** The composer's anchor chip was dismissed — clearing the ONE pending-anchor home
   *  removes the chip AND the draft mark on the media together. */
  onAnchorDismissed(): void {
    this.previewService.pendingAnchor.set(null);
  }

  /** Video previews stamp the playback moment by default: every comment is about a
   *  moment unless the author says otherwise (the chip's dismiss). The viewer answers
   *  the request — it owns the playhead — and applies the video/no-overwrite guards. */
  readonly videoTimestampReArm = computed<boolean>(() =>
    this.previewService.contentType() === 'video'
    && commentsArmed(this.previewService.context())
    && !this.temporal.active());

  onComposerFocusChange(focused: boolean): void {
    if (focused) this.onTimestampCaptureRequest();
  }

  onTimestampCaptureRequest(): void {
    this.previewService.timestampCaptureRequested.update(n => n + 1);
  }

  /** A prior-round chip was activated — route to the context (Reviews opens the compare). */
  onRoundTagClick(comment: Comment): void {
    this.previewService.context()?.onCommentRoundTagClick?.(comment);
  }

  /** The annotation whose chip or pin is hovered/activated — shared with the viewer. */
  readonly activeAnchorId = computed<string | null>(() => this.previewService.activeAnnotationId());

  /** An anchor chip was activated — ask the viewer to show the annotation. */
  onAnchorNavigate(comment: Comment): void {
    if (!comment.anchor) return;
    this.previewService.requestedAnchorNavigation.set({ commentId: comment.id, anchor: comment.anchor });
  }

  /** Chip hover — mirror the highlight onto the viewer's overlay pin. */
  onAnchorHoverChange(ev: { comment: Comment; hovering: boolean }): void {
    this.previewService.activeAnnotationId.set(ev.hovering ? ev.comment.id : null);
  }

  // ============================================================================
  // Similar items tab — lazy-loaded on tab activation. Backend dispatches by
  // source ItemType; results come back as DisplaySearchResult ready for
  // <app-search-result-card>. Re-fetches when the previewed item changes while
  // the tab is open.
  // ============================================================================

  readonly similarResults = signal<DisplaySearchResult[]>([]);
  readonly similarLoading = signal(false);
  private similarLoadedFor: string | null = null;
  /** The content lens the similar-items list was last read in — the latch's second key (a move re-reads). */
  private similarLens: string | null = null;

  /**
   * Adapts DisplaySearchResult[] to the MediaMasonryComponent MediaItem shape.
   * MediaMasonry resolves thumbnails via `getPreviewUrl(item, 'medium', config)`
   * which reads `fileData.previews.medium.url` and wraps with storage URL —
   * so we feed the **raw** thumbnailUrl (not the already-resolved DisplaySearchResult.thumbnail)
   * into `fileData.previews.medium.url` and let the masonry resolve it normally.
   */
  readonly similarMosaicItems = computed(() =>
    this.similarResults().map(r => ({
      id: r.id,
      name: r.name,
      title: r.title,
      type: r.type as unknown as number,
      fileData: r.thumbnailUrl
        ? { previews: { medium: { url: r.thumbnailUrl } } }
        : undefined,
    }))
  );

  constructor() {
    this.destroyRef.onDestroy(() => { this.destroyed = true; });

    // Context-chosen landing tab (Reviews lands on the feedback thread). Guarded by the
    // same visibility gate as the one-shot request below — a hidden comments tab
    // (e.g. "Time travel") keeps the 'info' default.
    const initialTab = this.previewService.context()?.infoOptions?.initialTab;
    if (initialTab === 'comments' && this.showComments()) {
      this.activeTab.set('comments');
    }

    // One-shot tab request from the viewer's annotate gesture: activeTab is LOCAL to this
    // component, so the gesture cannot select the Comments tab without this seam. Consume
    // and clear (the request must not re-fire on a later panel remount).
    effect(() => {
      const requested = this.previewService.requestedPanelTab();
      if (!requested) return;
      untracked(() => {
        if (requested === 'comments' && this.showComments()) this.activeTab.set('comments');
        this.previewService.requestedPanelTab.set(null);
      });
    });

    // The similar-items list is read in the content lens once per item; a lens move with the tab on screen
    // makes it a stale answer. The search page's shape (ruled 2026-09-16): the latch keys on the item AND the
    // lens the list was read in, so a move with the tab on screen re-reads SILENTLY, the same lens again
    // re-reads nothing, and a tab off screen re-reads when it is next opened.
    effect(() => {
      const current = this.item();
      const tab = this.activeTab();
      const lens = this.localeService.activeContentLanguage();
      if (tab !== 'similar' || !current?.id) return;
      if (this.similarLoadedFor === current.id && this.similarLens === lens) return;
      void this.loadSimilar(current.id, lens);
    });

    // Version-existence probe for the Versions tab gate. SoftLinks probe the TARGET
    // (versions live there). Filmstrip races guarded by re-checking the current id.
    effect(() => {
      const current = this.item();
      const inMode = this.temporal.active();
      if (!current?.id || inMode || (!isFile(current) && !current.target)) {
        untracked(() => this.hasPriorVersions.set(false));
        return;
      }
      const probedId = current.id;
      const targetId = current.target?.id ?? current.id;
      untracked(() => {
        void this.itemHistory.getFileVersionsCached(targetId)
          .then(page => {
            if (this.item()?.id === probedId) this.hasPriorVersions.set(page.totalCount > 1);
          })
          .catch(() => {
            if (this.item()?.id === probedId) this.hasPriorVersions.set(false);
          });
      });
    });

    // Load campaign back-references whenever a file item is shown (once per id).
    // In-mode the list is a NOW-clock surface beside asOf content — same gate as
    // its Comments/Versions siblings.
    effect(() => {
      const current = this.item();
      if (!current?.id || !isFile(current) || this.temporal.active()) {
        this.campaignsUsing.set([]);
        return;
      }
      if (this.campaignsLoadedFor === current.id) return;
      void this.loadCampaignsUsing(current.id);
    });
  }

  /** Fetch the campaigns referencing this asset. Planned: its chip — until it renders somewhere, a
   *  failure leaves the list empty and says nothing (there is no surface to say it on). */
  private async loadCampaignsUsing(itemId: string): Promise<void> {
    this.campaignsLoadedFor = itemId;
    this.campaignsUsing.set([]);
    try {
      const res = await this.graphql.queryAsync<CampaignsUsingAssetResponse>(CAMPAIGNS_USING_ASSET, { assetId: itemId });
      if (this.item()?.id === itemId) this.campaignsUsing.set(res.campaignsUsingAsset ?? []);
    } catch {
      if (this.campaignsLoadedFor === itemId) this.campaignsLoadedFor = null;
    }
  }

  private async loadSimilar(itemId: string, lens: string): Promise<void> {
    this.similarLoading.set(true);
    this.similarResults.set([]);
    this.similarLoadedFor = itemId;
    this.similarLens = lens;
    try {
      const results = await this.itemService.getSimilarItems(itemId, 12);
      // Guard against the item changing during the in-flight call.
      if (this.item()?.id === itemId) {
        this.similarResults.set(results);
      }
    } catch (error) {
      // Reset the guard so the next tab open retries.
      if (this.similarLoadedFor === itemId) this.similarLoadedFor = null;
      const detail = error instanceof MutationError
        ? error.userErrors
            .map(e => resolveErrorMessage(e, (k, p) => this.translateService.instant(k, p)))
            .join('; ')
        : this.translateService.instant('filePreview.sidebar.similarLoadFailed');
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail,
      });
    } finally {
      this.similarLoading.set(false);
    }
  }

  async onSimilarClicked(event: { item: { id: string } }): Promise<void> {
    const item = await this.itemService.getItem(event.item.id);
    if (!item) return;
    // Swap the preview to the clicked item — single-item mode (no filmstrip
    // navigation context since the new item isn't in the original collection).
    // The pillar context must not follow: the clicked item is outside the world
    // that context governs (its anchors, action bar and arming would leak onto an
    // unrelated item), so the re-entry is a plain, fully-armed preview.
    // Permissions/canEdit are re-derived server-side via `effectiveRole`; pass
    // `false` here since we don't have a stronger signal for the clicked item.
    this.previewService.setContext(null);
    this.previewService.open({ item, canEdit: false });
  }

  /**
   * Breadcrumb entries normalized to a filtered array. Single-string contexts produce a
   * one-entry array; readonly-string-array contexts pass through after empty-entry filter.
   * The template renders the first entry as primary (parent — bolder/larger) and any
   * subsequent entries as secondary (descendants/path-tail).
   */
  readonly breadcrumbItems = computed<readonly string[]>(() => {
    const b = this.previewService.context()?.infoOptions?.breadcrumb;
    if (!b) return [];
    const arr = typeof b === 'string' ? [b] : b;
    return arr.filter(s => s && s.trim().length > 0);
  });

  /**
   * Effective edit permission for the rendered fields.
   *
   * The `canEdit` input is consumer-supplied (via `FilePreviewService.open({canEdit})`)
   * and historically derived from the container's effectiveRole — Drive passes
   * `can.edit(currentFolder.effectiveRole)`, which can be wrong/stale when folder-level
   * permissions don't reflect the item's own. The fix: gate on the previewed item's own
   * `can.edit(item.effectiveRole)` AND honor the consumer's hard-ceiling.
   *
   * Truth table:
   *   - canEdit input false → false (consumer says no, never edit)
   *   - canEdit input true + item lacks 'edit' perm → false (item denies)
   *   - canEdit input true + item has 'edit' perm → true (both agree)
   *
   * Same pattern as `DetailsSidebarComponent.effectiveCanEdit`.
   */
  readonly effectiveCanEdit = computed<boolean>(() => {
    if (!this.canEdit()) return false;
    // A compliance-locked item suppresses ALL edit affordances (pencils, caption hint,
    // requiresEdit secondary action) — proactive gating instead of green-flash-then-revert;
    // the lock section below the fields explains the state. Stated deviation from workflow-lock
    // behavior (workflow lock state is not FE-queryable; compliance lock rides the full read).
    // isComplianceLocked also reads the GRID boolean, so suppression holds during the
    // list→full fetch window (the full-only object arrives later — or never, on fetch error).
    const item = this.item();
    if (item && isComplianceLocked(item)) return false;
    return can.edit(item?.effectiveRole);
  });

  /** Optional caption-hint helper text — rendered only when the user can edit. */
  readonly captionHint = computed<string | undefined>(() => {
    if (!this.effectiveCanEdit()) return undefined;
    return this.previewService.context()?.infoOptions?.captionHint;
  });

  /** Optional secondary action button. Returns undefined when hidden by gates. */
  readonly secondaryAction = computed<FilePreviewInfoOptions['secondaryAction'] | undefined>(() => {
    const action = this.previewService.context()?.infoOptions?.secondaryAction;
    if (!action) return undefined;
    if (action.requiresEdit && !this.effectiveCanEdit()) return undefined;
    const item = this.item();
    if (action.visibleIf && !action.visibleIf(item)) return undefined;
    return action;
  });

  onSecondaryActionClick(): void {
    const action = this.secondaryAction();
    if (action) action.onClick(this.item());
  }

  // Icons
  readonly faDownload = faDownload;
  readonly faBullhorn = faBullhorn;

  protected readonly locale = inject(LocaleService).activeContentLanguage;

  // Inputs
  readonly item = input.required<Item>();
  /** The publisher's third state for the three faces: the item is unpublished in the lens for a viewer who can see it (root parity). */
  protected readonly unpublishedInLens = computed(() => shouldShowUnpublishedIndicator(this.item(), this.locale(), this.access.isRoot()));
  readonly downloadOptions = input<DownloadOption[]>([]);
  readonly canEdit = input(false);
  readonly contentType = input('unknown');
  readonly compact = input(false); // For mobile collapsed state
  // Forwarded to the comment thread (no computation here — the preview host owns the
  // permission collapse, like [canEdit]): may the member post / moderate-any, and are
  // anchor chips live navigation on this surface.
  readonly canPost = input(true);
  readonly canModerate = input(true);
  readonly anchorsNavigable = input(true);

  // Outputs
  readonly download = output<string | undefined>();
  readonly itemUpdated = output<Item>();

  // Computed
  readonly contentLanguage = computed(() =>
    this.localeService.getActiveContentLanguage()
  );

  // ============================================================================
  // Field save handlers (Title / Caption / Description)
  // Uniform optimistic shape: snapshot → emit → mutate → rollback on error.
  // The `<app-label-field>` component handles the local edit lifecycle and emits a
  // MINIMAL per-locale merge patch. The optimistic emit merges the patch into the
  // item's full dictionary (the UI needs the complete dict); the WIRE carries only
  // the patch — the server's per-locale write is MERGE + explicit-empty-to-clear
  // (ItemAttributeRepository.UpdateTitleAttributeAsync family), so untouched locales
  // stay untouched and the temporal trail records only the locale actually edited.
  // ============================================================================

  private toLocaleArray(value: LocalizedValue): { locale: string; value: string }[] {
    return Object.entries(value).map(([locale, v]) => ({ locale, value: v }));
  }

  /**
   * Resolves true when the write LANDED, false when it was refused (the rollback and its toast already spoken —
   * or, given `refused`, the refusal's sentence handed to that caller, which owns the voice: the translate door's
   * restore face).
   */
  private async optimisticUpdate(
    patch: { title?: LocalizedValue; caption?: LocalizedValue; description?: LocalizedValue },
    errorKey: string,
    refused?: (detail: string) => void,
  ): Promise<boolean> {
    const snapshot = this.item();
    this.itemUpdated.emit({
      ...snapshot,
      ...(patch.title && { title: { ...(snapshot.title ?? {}), ...patch.title } }),
      ...(patch.caption && { caption: { ...(snapshot.caption ?? {}), ...patch.caption } }),
      ...(patch.description && { description: { ...(snapshot.description ?? {}), ...patch.description } }),
    });

    try {
      await this.itemService.updateItem({
        itemId: snapshot.id,
        title: patch.title ? this.toLocaleArray(patch.title) : undefined,
        caption: patch.caption ? this.toLocaleArray(patch.caption) : undefined,
        description: patch.description ? this.toLocaleArray(patch.description) : undefined,
      });
      return true;
    } catch (error) {
      // Only roll back if the user is still viewing the item we mutated. If they navigated
      // next/prev mid-flight, `this.item().id` has changed; emitting `snapshot` would
      // overwrite the now-current item with stale data.
      if (this.item()?.id === snapshot.id) {
        // The rollback emit has nobody to reach after teardown (the host is gone; an output
        // cannot deliver once destroyed).
        if (!this.destroyed) this.itemUpdated.emit(snapshot);
      }

      // The failure TOAST never stands down: it renders through the ONE app-level mount, so the
      // failure shows wherever the member is now. A save nobody can see must never fail
      // silently (ruled 2026-09-01) — an out-of-context toast is the accepted trade.
      const detail = error instanceof MutationError
        ? error.userErrors
            .map(e => resolveErrorMessage(e, (k, p) => this.translateService.instant(k, p)))
            .join('; ')
        : this.translateService.instant(errorKey);

      // A caller that owns the refusal's voice (the translate door's restore face) takes the sentence instead.
      if (refused) { refused(detail); return false; }
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail,
      });
      return false;
    }
  }

  /**
   * An inline commit (ruled 2026-09-16, the closed-dialog draft-loss class): the field's submission is the patch beside
   * its restore door, and a refusal speaks the rollback sentence WITH that door — "Restore what you typed" re-enters the
   * field's edit seeded with the refused text — so nothing typed is lost to a refusal the member did not cause.
   */
  async onTitlePatched(submission: LabelSubmission): Promise<void> {
    await this.optimisticUpdate({ title: submission.patch }, 'filePreview.titleSaveError',
      detail => raiseDraftRestoreToast(this.actionToastService, this.translateService, detail, submission.restore));
  }

  /**
   * The translate door's one-language patch: the same optimistic write an inline commit rides, and the ruled
   * saved sentence ("Saved. Members reading in Czech see it now.") raised only once the write LANDS — never
   * before the server's word (ruled 2026-09-12); a refusal speaks the rollback sentence WITH the restore door
   * (ruled 2026-09-14: "Restore what you typed" reopens the dialog pre-filled through the submission). This pane
   * is inside the per-locale PUBLISH model, so it hands the item's `published` map to the one raise home (ruled
   * 2026-09-14): an item unpublished in the target language speaks "Publish Czech to show it to members." — the
   * map captured BEFORE the write, so a next/prev navigation mid-flight cannot swap in another item's map.
   */
  onTranslated(field: 'title' | 'caption' | 'description', submission: LabelSubmission): Promise<void> {
    const published = this.item()?.published;
    return this.optimisticUpdate({ [field]: submission.patch }, TRANSLATE_SAVE_ERROR_KEYS[field],
      detail => raiseDraftRestoreToast(this.actionToastService, this.translateService, detail, submission.restore),
    ).then(landed => {
      if (landed) raiseTranslationSavedToast(this.messageService, this.translateService, this.localeService, submission.patch, { published });
    });
  }

  async onCaptionPatched(submission: LabelSubmission): Promise<void> {
    await this.optimisticUpdate({ caption: submission.patch }, 'filePreview.captionSaveError',
      detail => raiseDraftRestoreToast(this.actionToastService, this.translateService, detail, submission.restore));
  }

  async onDescriptionPatched(submission: LabelSubmission): Promise<void> {
    await this.optimisticUpdate({ description: submission.patch }, 'filePreview.descriptionSaveError',
      detail => raiseDraftRestoreToast(this.actionToastService, this.translateService, detail, submission.restore));
  }

  // ============================================================================
  // Downloads
  // ============================================================================

  onDownload(size?: string): void {
    this.download.emit(size);
  }
}
